import { Network } from 'lucide-react'
import type { FdeCopy, ModelElement, Relationship } from '@/frontend/features/Fde/types'

interface Props {
  person: ModelElement
  elements: ModelElement[]
  relationships: Relationship[]
  copy: FdeCopy
  onSelectNode: (id: string) => void
}

interface LinkedElement {
  element: ModelElement
  relationship: Relationship
}

function elementIs(element: ModelElement, classKey: string) {
  return element.ontology_class?.lineage.includes(classKey) || (element.ontology_class?.key || element.kind) === classKey
}

function normalizedVerb(value: string) {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleUpperCase()
}

function linksFor(person: ModelElement, elements: ModelElement[], relationships: Relationship[]) {
  const personId = String(person.id)
  const byId = new Map(elements.map((element) => [String(element.id), element]))
  const outgoing = (verb: string, classKey: string) => relationships.flatMap((relationship) => {
    if (String(relationship.from.id) !== personId || normalizedVerb(relationship.verb) !== verb) return []
    const element = byId.get(String(relationship.to.id))
    return element && elementIs(element, classKey) ? [{ element, relationship }] : []
  })
  const incoming = (verb: string, classKey: string) => relationships.flatMap((relationship) => {
    if (String(relationship.to.id) !== personId || normalizedVerb(relationship.verb) !== verb) return []
    const element = byId.get(String(relationship.from.id))
    return element && elementIs(element, classKey) ? [{ element, relationship }] : []
  })

  return {
    managers: outgoing('REPORTA A', 'pessoa'),
    teams: outgoing('PERTENCE A', 'equipe'),
    roles: outgoing('EXERCE', 'papel'),
    directReports: incoming('REPORTA A', 'pessoa'),
    processes: [...outgoing('RESPONSAVEL POR', 'processo'), ...outgoing('PARTICIPA DE', 'processo')],
  }
}

function LinkGroup({ label, links, emptyLabel, onSelectNode }: { label: string; links: LinkedElement[]; emptyLabel?: string; onSelectNode: (id: string) => void }) {
  if (links.length === 0 && !emptyLabel) return null
  return <div className="min-w-0">
    <dt className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</dt>
    <dd className="mt-1 flex flex-wrap gap-1">
      {links.length === 0 ? <span className="text-[10px] text-muted-foreground">{emptyLabel}</span> : links.map(({ element, relationship }) => (
        <button key={relationship.id} type="button" title={relationship.verb} onClick={() => onSelectNode(String(element.id))} className="max-w-44 truncate rounded border border-border bg-background px-1.5 py-1 text-[10px] font-medium text-foreground hover:border-primary/40">
          {element.name}
        </button>
      ))}
    </dd>
  </div>
}

export default function PersonOrganizationContext({ person, elements, relationships, copy, onSelectNode }: Props) {
  if (!elementIs(person, 'pessoa')) return null
  const links = linksFor(person, elements, relationships)

  return <section className="mt-2 rounded-md border border-border bg-muted/10 px-2.5 py-2" data-testid="person-organization-context">
    <h3 className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-foreground"><Network className="size-3 text-primary" />{copy.workspace.organization_context}</h3>
    <dl className="mt-2 grid gap-3 sm:grid-cols-2">
      <LinkGroup label={copy.workspace.organization_manager} links={links.managers} emptyLabel={copy.workspace.organization_not_informed} onSelectNode={onSelectNode} />
      <LinkGroup label={copy.workspace.organization_team} links={links.teams} emptyLabel={copy.workspace.organization_not_informed} onSelectNode={onSelectNode} />
      <LinkGroup label={copy.workspace.organization_roles} links={links.roles} onSelectNode={onSelectNode} />
      <LinkGroup label={copy.workspace.organization_direct_reports} links={links.directReports} onSelectNode={onSelectNode} />
      <LinkGroup label={copy.workspace.organization_processes} links={links.processes} onSelectNode={onSelectNode} />
    </dl>
  </section>
}
